import React, { useState, useEffect, useContext, useMemo } from "react";
import { GlobalContext } from "../../Context/GlobalContext";
import "../Sales Person/CreateSalesPerson.css";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
} from "recharts";

const SalesChartData = ({
  selectedProductProp = "",
  chartTypeProp = "Business",
  onChartDataChange,
}) => {
  const { api } = useContext(GlobalContext);

  const sessionAdminId = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("userData");
      if (!raw) return "";
      const parsed = JSON.parse(raw);
      return parsed?.adminMeta?.adminId ? String(parsed.adminMeta.adminId) : "";
    } catch {
      return "";
    }
  }, []);

  const effectiveAdminId = sessionAdminId;

  const [chartType, setChartType] = useState(chartTypeProp);
  const [chartData, setChartData] = useState([]);
  const [loadingChart, setLoadingChart] = useState(false);
  const [chartTypeOptions, setChartTypeOptions] = useState([]);

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const [chartFromDate, setChartFromDate] = useState(getTodayDate());
  const [chartToDate, setChartToDate] = useState(getTodayDate());
  const [selectedProduct, setSelectedProduct] = useState(selectedProductProp);

  useEffect(() => {
    if (selectedProductProp) setSelectedProduct(selectedProductProp);
  }, [selectedProductProp]);

  useEffect(() => {
    if (chartTypeProp) setChartType(chartTypeProp);
  }, [chartTypeProp]);

  const GetSalesChartData = async (
    adminIdParam,
    productId,
    type = "Business",
    from = "",
    to = ""
  ) => {
    if (!adminIdParam || !productId) {
      setChartData([]);
      return;
    }

    setLoadingChart(true);

    try {
      const payload = {
        adminId: String(adminIdParam),
        productId: String(productId),
        type,
        fromDate: from || "",
        toDate: to || "",
      };

      const res = await api.post("Admin/v1.0/getSalesChartDatav1", payload);

      if (res?.data?.responseCode === "00") {
        if (Array.isArray(res.data.availableTypes)) {
          setChartTypeOptions(res.data.availableTypes);
        }

        if (Array.isArray(res.data.data)) {
          const transformedData = res.data.data.map((item) => {
            let dateStr = item.date;
            if (dateStr?.includes(" ")) dateStr = dateStr.split(" ")[0];

            let dateObj;
            if (dateStr?.includes("/")) {
              const parts = dateStr.split("/");
              dateObj = new Date(
                parseInt(parts[2], 10),
                parseInt(parts[0], 10) - 1,
                parseInt(parts[1], 10)
              );
            } else {
              dateObj = new Date(dateStr);
            }

            const day = dateObj.getDate();
            const month = dateObj.toLocaleString("en-US", { month: "short" });
            const year = dateObj.getFullYear();

            const formattedDate = `${day}-${month}-${year}`;

            return {
              name: formattedDate,
              value: parseFloat(item.value) || 0,
              rawDate: dateObj,
            };
          });

          transformedData.sort((a, b) => a.rawDate - b.rawDate);

          setChartData(transformedData);
          if (onChartDataChange) onChartDataChange(transformedData);
        } else {
          setChartData([]);
        }
      } else {
        setChartData([]);
      }
    } catch (err) {
      setChartData([]);
    } finally {
      setLoadingChart(false);
    }
  };

  useEffect(() => {
    if (effectiveAdminId && selectedProduct) {
      GetSalesChartData(
        effectiveAdminId,
        selectedProduct,
        chartType,
        chartFromDate,
        chartToDate
      );
    }
  }, [
    effectiveAdminId,
    selectedProduct,
    chartType,
    chartFromDate,
    chartToDate,
  ]);

  return (
    <div className="bg-white rounded-lg shadow p-6 dark:bg-gray-800 transition-colors">
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Sales Chart Data
        </h2>
      </div>

      <div className="flex space-between justify-end gap-4 mb-4">
        <div className="aoc-csp-control-new">
          <select
            className="rounded px-3 py-1 text-sm aoc-csp-input"
            value={chartType}
            onChange={(e) => setChartType(e.target.value)}
          >
            {chartTypeOptions.length > 0
              ? chartTypeOptions.map((type, idx) => (
                  <option key={idx} value={type}>
                    {type}
                  </option>
                ))
              : ["Business", "DSR", "Onboarding"].map((type, idx) => (
                  <option key={idx} value={type}>
                    {type}
                  </option>
                ))}
          </select>
        </div>

        <div className="flex gap-4">
          <input
            type="date"
            value={chartFromDate}
            onChange={(e) => setChartFromDate(e.target.value)}
            className="custom-date-input bg-white dark:bg-gray-800 dark:text-gray-100 text-sm"
          />
          <input
            type="date"
            value={chartToDate}
            onChange={(e) => setChartToDate(e.target.value)}
            className="custom-date-input bg-white dark:bg-gray-800 dark:text-gray-100 text-sm"
          />
        </div>
      </div>

      {loadingChart ? (
        <div className="h-80 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : chartData.length === 0 ? (
        <div className="h-80 flex items-center justify-center text-gray-500">
          No data available
        </div>
      ) : (
        <div className="h-80 pt-5">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, angle: -45, textAnchor: "end" }}
                height={80}
                interval={0}
                minTickGap={0}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                formatter={(value) => {
                  const numericValue = Number(value) || 0;
                  return numericValue.toLocaleString();
                }}
                labelFormatter={() => ""}
                contentStyle={{
                  backgroundColor: "#ffffff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "6px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
                cursor={false}
              />
              <Bar
                dataKey="value"
                fill="#10B981"
                radius={[4, 4, 0, 0]}
                barSize={chartData.length > 20 ? 30 : 45}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default SalesChartData;
